import React, { useState } from "react";
import { Pagination } from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG } from "@/types";
import { cn } from "@/lib/utils";
import { MdOutlineBlock } from "react-icons/md";
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface Column {
  title: string | React.ReactNode;
  field: string;
  render?: (rowData: any) => React.ReactNode;
  hideOnMobile?: boolean;
  sortable?: boolean;
}

interface TableComponentProps {
  columns: Column[];
  data: any[];
  onRowClick?: (row: any) => void;
  rowKeyField: string;
  maxHeight?: number;
  isMobile?: boolean;
  rowsPerPage?: number;
  pagination?: boolean;
  searchable?: boolean;
  sortable?: boolean;
  title?: string;
  subtitle?: string;
  emptyStateIcon?: React.ReactNode;
  emptyStateMessage?: string;
  emptyStateSubmessage?: string;
  variant?: "default" | "elevated" | "bordered";
  size?: "sm" | "md" | "lg";
}

const TableComponent: React.FC<TableComponentProps> = ({
  columns,
  data,
  onRowClick,
  rowKeyField,
  maxHeight = 400,
  rowsPerPage = 10,
  pagination = false,
  searchable = false,
  sortable = false,
  title,
  subtitle,
  emptyStateIcon,
  emptyStateMessage,
  emptyStateSubmessage,
  variant = "default",
  size = "md",
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const totalPages = Math.ceil(data.length / rowsPerPage);

  // Filter data based on search term
  const filteredData =
    searchable && searchTerm
      ? data.filter((row) =>
          Object.values(row).some((value) =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
          )
        )
      : data;

  // Sort data
  const sortedData =
    sortable && sortField
      ? [...filteredData].sort((a, b) => {
          const aValue = a[sortField];
          const bValue = b[sortField];

          if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
          if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
          return 0;
        })
      : filteredData;

  const paginatedData = pagination
    ? sortedData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : sortedData;

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="w-4 h-4" />
    ) : (
      <ArrowDown className="w-4 h-4" />
    );
  };

  const getVariantClasses = () => {
    switch (variant) {
      case "elevated":
        return "bg-white border-0 rounded-2xl";
      case "bordered":
        return "bg-white border-2 border-gray-200 rounded-xl";
      default:
        return "bg-white border border-gray-100 rounded-xl";
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "text-sm";
      case "lg":
        return "text-lg";
      default:
        return "text-base";
    }
  };

  const toggleRowExpansion = (rowId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Filter columns for mobile (hide columns marked as hideOnMobile)
  const mobileColumns = columns.filter((col) => !col.hideOnMobile);
  const desktopColumns = columns;

  return (
    <div
      className={cn("overflow-hidden", getVariantClasses(), getSizeClasses())}
      style={{
        direction: locale === LANG.AR ? "rtl" : "ltr",
      }}
    >
      {/* Header Section */}
      {(title || subtitle || searchable) && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              {title && (
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                  {title}
                </h3>
              )}
              {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
            </div>
            {searchable && (
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t("actions.searching")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block">
        <div className="relative">
          {/* Fixed Header */}
          <div
            className="sticky top-0 z-10 bg-gradient-to-r border-l border-r border-b border-blue-200"
            style={{
              gridTemplateColumns: `repeat(${desktopColumns.length}, 1fr)`,
              display: "grid",
              minWidth: "100%",
            }}
          >
            {desktopColumns.map((column, index) => (
              <div
                key={index}
                className={cn(
                  "p-4 font-semibold text-gray-700 flex items-center gap-2 transition-colors duration-200",
                  column.field === "checkbox" && "min-w-16",
                  sortable &&
                    column.sortable &&
                    "cursor-pointer hover:bg-blue-100"
                )}
                onClick={() =>
                  sortable && column.sortable && handleSort(column.field)
                }
              >
                <span>{column.title}</span>
                {sortable && column.sortable && getSortIcon(column.field)}
              </div>
            ))}
          </div>

          {/* Scrollable Body */}
          <div style={{ maxHeight, overflowY: "auto" }}>
            {paginatedData.length > 0 ? (
              <div className="w-full">
                {/* Data Rows */}
                {paginatedData?.map((row, rowIndex) => (
                  <div
                    key={row[rowKeyField]}
                    className={cn(
                      "grid w-full border-b border-gray-100 transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50",
                      onRowClick && "cursor-pointer",
                      rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    )}
                    style={{
                      gridTemplateColumns: `repeat(${desktopColumns.length}, 1fr)`,
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {desktopColumns?.map((column, colIndex) => (
                      <div
                        key={colIndex}
                        className={cn(
                          "p-4 flex items-center transition-colors duration-200",
                          column.field === "checkbox" && "md:min-w-20 min-w-10"
                        )}
                      >
                        {column.render ? (
                          column.render(row)
                        ) : (
                          <span className="text-gray-700">
                            {row[column.field] || "-"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State */
              <div className="flex flex-col items-center justify-center py-16 px-6">
                {emptyStateIcon || (
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                    <MdOutlineBlock className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {emptyStateMessage || t("general.NoDataAvailable")}
                </h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  {emptyStateSubmessage || t("general.noDataMatchingCriteria")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Table */}
      <div className="md:hidden">
        <div style={{ maxHeight, overflowY: "auto" }}>
          {paginatedData.length > 0 ? (
            <div className="space-y-3 p-4">
              {paginatedData?.map((row) => {
                const isExpanded = expandedRows.has(row[rowKeyField]);
                const hasHiddenColumns = columns.some(
                  (col) => col.hideOnMobile
                );

                return (
                  <div
                    key={row[rowKeyField]}
                    className={cn(
                      "bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200",
                      onRowClick && "cursor-pointer",
                      isExpanded && "ring-2 ring-blue-100"
                    )}
                  >
                    {/* Main Content */}
                    <div
                      className="p-4"
                      onClick={() => {
                        if (hasHiddenColumns) {
                          toggleRowExpansion(row[rowKeyField]);
                        } else {
                          onRowClick?.(row);
                        }
                      }}
                    >
                      {/* Primary Info Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          {/* First column as main title */}
                          {mobileColumns[0] && (
                            <div className="mb-1">
                              {mobileColumns[0].render ? (
                                mobileColumns[0].render(row)
                              ) : (
                                <div className="text-base font-semibold text-gray-900 truncate">
                                  {row[mobileColumns[0].field] || "-"}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Second column as subtitle */}
                          {mobileColumns[1] && (
                            <div className="text-sm text-gray-600">
                              {mobileColumns[1].render ? (
                                mobileColumns[1].render(row)
                              ) : (
                                <span>
                                  {row[mobileColumns[1].field] || "-"}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Expand button */}
                        {hasHiddenColumns && (
                          <button
                            className="ml-3 flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowExpansion(row[rowKeyField]);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Secondary Info Row */}
                      {mobileColumns[2] && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {mobileColumns[2].render ? (
                              mobileColumns[2].render(row)
                            ) : (
                              <span className="text-sm text-gray-700">
                                {row[mobileColumns[2].field] || "-"}
                              </span>
                            )}
                          </div>

                          {hasHiddenColumns && (
                            <span className="text-xs text-gray-400">
                              Tap for more
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && hasHiddenColumns && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        <div className="p-4">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                            Details
                          </div>
                          <div className="space-y-3">
                            {columns
                              .filter((col) => col.hideOnMobile)
                              .map((column, colIndex) => (
                                <div
                                  key={colIndex}
                                  className="flex items-center justify-between"
                                >
                                  <span className="text-sm text-gray-600 font-medium">
                                    {typeof column.title === "string"
                                      ? column.title
                                      : "Field"}
                                  </span>
                                  <div className="text-sm text-gray-900 text-right">
                                    {column.render ? (
                                      column.render(row)
                                    ) : (
                                      <span>{row[column.field] || "-"}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty State for Mobile */
            <div className="flex flex-col items-center justify-center py-16 px-4">
              {emptyStateIcon || (
                <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                  <MdOutlineBlock className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                {emptyStateMessage || t("general.NoDataAvailable")}
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">
                {emptyStateSubmessage || t("general.noDataMatchingCriteria")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600 text-center sm:text-left">
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredData.length)} of{" "}
              {filteredData.length} results
            </div>
            <div className="flex justify-center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    margin: "0 2px",
                    "&.Mui-selected": {
                      backgroundColor: "#644980",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#644980",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#dbeafe",
                      color: "#1d4ed8",
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
